import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError, AUTH_EXPIRED_EVENT } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);
  // Used to surface a one-time "session expired" toast on the login page.
  const justExpiredRef = useRef(false);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // Sliding session keep-alive: re-ping /auth/me every 15 minutes so the
  // backend re-issues a fresh 30-day cookie. This means an active or even
  // a long-idle browser tab will keep its session warm and never have to
  // log in again unless the user explicitly logs out.
  useEffect(() => {
    if (!user || user === false) return undefined;
    const id = setInterval(() => {
      refreshMe();
    }, 15 * 60 * 1000);
    // Also re-check immediately when the tab is brought back into focus
    // (covers "left the laptop open overnight" case).
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshMe();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, refreshMe]);

  // Global listener: if any API call returns 401, sign the user out silently.
  useEffect(() => {
    const handler = () => {
      // Only react if we currently *think* we are authenticated. Otherwise
      // this fires on the initial /me probe and would spam the login screen.
      setUser((cur) => {
        if (cur && cur !== false) {
          justExpiredRef.current = true;
        }
        return false;
      });
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  }, []);

  const consumeExpiredFlag = useCallback(() => {
    const v = justExpiredRef.current;
    justExpiredRef.current = false;
    return v;
  }, []);

  const login = async (email, password, remember = false) => {
    try {
      await api.post("/auth/login", { email, password, remember });
      // Fetch /me so we get cafe + subscription info attached.
      const me = await refreshMe();
      return me ? { ok: true } : { ok: false, error: "Failed to load profile" };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const phonePasswordLogin = async (countryCode, phone, password, remember = false) => {
    try {
      await api.post("/auth/phone-password-login", {
        country_code: countryCode,
        phone,
        password,
        remember,
      });
      const me = await refreshMe();
      return me ? { ok: true } : { ok: false, error: "Failed to load profile" };
    } catch (e) {
      return { ok: false, error: formatApiError(e), status: e?.response?.status };
    }
  };

  const phoneLogin = async (firebaseIdToken, remember = false) => {
    try {
      await api.post("/auth/phone-login", {
        firebase_id_token: firebaseIdToken,
        remember,
      });
      const me = await refreshMe();
      return me ? { ok: true } : { ok: false, error: "Failed to load profile" };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout failed", e);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, phoneLogin, phonePasswordLogin, logout, refreshMe, consumeExpiredFlag }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
